import { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { has, isObject } from "lodash";
import { AuthContext } from "../../../context/Auth/AuthContext";
import { SocketContext } from "../../../context/Socket/SocketContext";
import {
  listChats,
  listChatMessages,
  sendChatMessage,
  deleteChat as deleteChatApi,
} from "../../../services/chats.api";

const useChatPage = ({ scrollToBottomRef, onTabSwitch, onNavigate }) => {
  const { user } = useContext(AuthContext);
  const socketManager = useContext(SocketContext);
  const { id } = useParams();

  const [chats, setChats] = useState([]);
  const [chatsPageInfo, setChatsPageInfo] = useState({ hasMore: false });
  const [messages, setMessages] = useState([]);
  const [messagesPageInfo, setMessagesPageInfo] = useState({ hasMore: false });
  const [messagesPage, setMessagesPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [currentChat, setCurrentChat] = useState({});
  const isMounted = useRef(true);

  // Lifecycle: flag unmount so async callbacks skip stale state updates
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Initial load: fetch all chats; auto-select if URL contains a chat id
  useEffect(() => {
    if (isMounted.current) {
      findChats().then((data) => {
        if (!data) return;
        const { records } = data;
        if (records.length > 0) {
          setChats(records);
          setChatsPageInfo(data);
          if (id && records.length) {
            const chat = records.find((r) => r.uuid === id);
            if (chat) selectChat(chat);
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load messages whenever the active chat changes
  useEffect(() => {
    if (isObject(currentChat) && has(currentChat, "id")) {
      findMessages(currentChat.id).then(() => {
        if (typeof scrollToBottomRef.current === "function") {
          setTimeout(() => {
            scrollToBottomRef.current();
          }, 300);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChat]);

  // Socket listeners — INVARIANT: use socket.off() only, never socket.disconnect()
  // because the socket instance is shared across the entire app via socketManager.
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    console.log("💬 Chat: useEffect ejecutado - socket:", socket?.socket?.connected);

    socket.on(`company-${companyId}-chat-user-${user.id}`, (data) => {
      if (data.action === "create") {
        setChats((prev) => {
          const exists = prev.some(chat => chat.id === data.record.id);
          if (exists) {
            console.log("⚠️ Conversación ya existe en la lista, evitando duplicado:", data.record.id);
            return prev;
          }
          console.log("✅ Agregando nueva conversación a la lista:", data.record.id);
          return [data.record, ...prev];
        });
      }
      if (data.action === "update") {
        const changedChats = chats.map((chat) => {
          if (chat.id === data.record.id) {
            setCurrentChat(data.record);
            return { ...data.record };
          }
          return chat;
        });
        setChats(changedChats);
      }
    });

    socket.on(`company-${companyId}-chat`, (data) => {
      if (data.action === "create") {
        setChats((prev) => {
          const exists = prev.some(chat => chat.id === data.record.id);
          if (exists) {
            console.log("⚠️ Conversación ya existe en la lista (evento general), evitando duplicado:", data.record.id);
            return prev;
          }
          console.log("✅ Agregando nueva conversación desde evento general:", data.record.id);
          return [data.record, ...prev];
        });
      }
      if (data.action === "delete") {
        const filteredChats = chats.filter((c) => c.id !== +data.id);
        setChats(filteredChats);
        setMessages([]);
        setMessagesPage(1);
        setMessagesPageInfo({ hasMore: false });
        setCurrentChat({});
        if (currentChat.id === +data.id) {
          onTabSwitch(0);
          onNavigate("/chats");
        }
      }
    });

    // Dynamic channel: only active when a specific chat is open
    if (isObject(currentChat) && has(currentChat, "id")) {
      socket.on(`company-${companyId}-chat-${currentChat.id}`, (data) => {
        if (data.action === "new-message") {
          setMessages((prev) => [...prev, data.newMessage]);
          const changedChats = chats.map((chat) => {
            if (chat.id === data.newMessage.chatId) {
              return { ...data.chat };
            }
            return chat;
          });
          setChats(changedChats);
          scrollToBottomRef.current();
        }
        if (data.action === "update") {
          const changedChats = chats.map((chat) => {
            if (chat.id === data.chat.id) {
              return { ...data.chat };
            }
            return chat;
          });
          setChats(changedChats);
          scrollToBottomRef.current();
        }
      });
    }

    socket.on(`company-${companyId}-appMessage`, (data) => {
      console.log("🔔 Chat: Recibido appMessage:", data.action);
      if (data.action === "create") {
        const message = data.message;
        const chat = data.chat;
        if (!chat || !chat.id) {
          console.log("⚠️ Chat: Datos de chat inválidos, ignorando mensaje");
          return;
        }
        if (isObject(currentChat) && has(currentChat, "id") && currentChat.id === chat.id) {
          console.log("✅ Chat: Agregando mensaje al chat actual:", message.id);
          setMessages((prev) => [...prev, message]);
          setChats((prevChats) => prevChats.map((c) => {
            if (c.id === chat.id) {
              return {
                ...c,
                lastMessage: message.body,
                lastMessageAt: message.createdAt,
                unreadMessages: c.unreadMessages + 1,
              };
            }
            return c;
          }));
          if (typeof scrollToBottomRef.current === "function") {
            setTimeout(() => {
              scrollToBottomRef.current();
            }, 100);
          }
        } else {
          console.log("ℹ️ Chat: Mensaje recibido para otro chat:", chat.id);
        }
      }
    });

    return () => {
      // ✅ INVARIANTE: remove specific listeners; never disconnect the shared socket
      if (socket && typeof socket.off === "function") {
        socket.off(`company-${companyId}-chat-user-${user.id}`);
        socket.off(`company-${companyId}-chat`);
        if (isObject(currentChat) && has(currentChat, "id")) {
          socket.off(`company-${companyId}-chat-${currentChat.id}`);
        }
        socket.off(`company-${companyId}-appMessage`);
        socket.off(`company-${companyId}-contact`);
        socket.off("ready");
        socket.off("connect");
        socket.off("disconnect");
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChat, socketManager]);

  const findChats = async () => {
    try {
      const { data } = await listChats();
      return data;
    } catch (err) {
      console.log(err);
    }
  };

  const findMessages = async (chatId) => {
    setLoading(true);
    try {
      const { data } = await listChatMessages(chatId, messagesPage);
      setMessagesPage((prev) => prev + 1);
      setMessagesPageInfo(data);
      setMessages((prev) => [...data.records, ...prev]);
    } catch (err) {}
    setLoading(false);
  };

  const loadMoreMessages = async () => {
    if (!loading) {
      findMessages(currentChat.id);
    }
  };

  const selectChat = (chat) => {
    try {
      setMessages([]);
      setMessagesPage(1);
      setCurrentChat(chat);
      onTabSwitch(1);
    } catch (err) {}
  };

  const sendMessage = async (contentMessage) => {
    setLoading(true);
    try {
      await sendChatMessage(currentChat.id, {
        message: contentMessage,
      });
    } catch (err) {}
    setLoading(false);
  };

  const deleteChat = async (chat) => {
    try {
      await deleteChatApi(chat.id);
      const updatedChats = chats.filter(c => c.id !== chat.id);
      setChats(updatedChats);
      if (currentChat.id === chat.id) {
        setCurrentChat({});
        setMessages([]);
        setMessagesPage(1);
        setMessagesPageInfo({ hasMore: false });
        onTabSwitch(0);
        onNavigate("/chats");
      }
      console.log("✅ Conversación eliminada correctamente:", chat.title);
    } catch (err) {
      console.error("❌ Error al eliminar conversación:", err);
    }
  };

  // Called by ChatModal after creating a new conversation
  const afterChatCreated = (data) => {
    setMessages([]);
    setMessagesPage(1);
    setCurrentChat(data);
    findChats().then((chatData) => {
      if (!chatData) return;
      const { records } = chatData;
      if (records.length > 0) {
        setChats(records);
        setChatsPageInfo(chatData);
        console.log("✅ Lista de chats actualizada después de crear nueva conversación");
      }
    }).catch(err => {
      console.error("❌ Error al refrescar lista de chats:", err);
    });
  };

  return {
    chats,
    chatsPageInfo,
    messages,
    messagesPageInfo,
    loading,
    currentChat,
    selectChat,
    sendMessage,
    deleteChat,
    loadMoreMessages,
    findChats,
    afterChatCreated,
  };
};

export default useChatPage;
